/**
 * HomeDocCard — one document on Home's "Recent" list and the Drafts tab.
 *
 * The design system's DocCard (boards "02 Home" and "38 Drafts"): doctype
 * tag and status pill, the draft's name, "library · date", then sections
 * done as a progress bar. Clicking the card loads the draft into Word; the
 * ⋯ menu (Drafts tab) renames, opens, downloads or files it.
 */

import * as React from "react";
import { Icon, ProgressBar, StatusPill, Tag, type PillStatus } from "../../ds/components";
import type { DocState, HomeDoc } from "../homeSceneMapping";

/** "2 Sept 2026" — the boards' date style. Empty when missing/invalid. */
export function formatCardDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" });
}

/** The card's status pill: design-system status + its short label. Pure. */
export function docCardPill(state: DocState, stateLabel: string): { status: PillStatus; label: string } {
  if (stateLabel === "Failed") return { status: "failed", label: "Failed" };
  if (stateLabel === "Cancelled") return { status: "queued", label: "Cancelled" };
  switch (state) {
    case "drafting":
      return { status: "drafting", label: "Drafting" };
    case "review":
      return { status: "paused", label: "Needs review" };
    case "indexing":
      return { status: "queued", label: stateLabel === "—" ? "Queued" : stateLabel };
    default:
      return { status: "done", label: "Completed" };
  }
}

interface HomeDocCardProps {
  doc: HomeDoc;
  onLoad: (doc: HomeDoc) => void;
  loading?: boolean;
  /** Drafts tab: open the run's .docx in a new Word window. */
  onOpenExternal?: (doc: HomeDoc) => void;
  /** Drafts tab: download the run's audit bundle (.zip). */
  onDownloadBundle?: (doc: HomeDoc) => void;
  /** Which overflow action is mid-flight ("Opening…" / "Zipping…"). */
  menuBusy?: "open" | "bundle" | null;
  /** Adds "Rename…" to the ⋯ menu; edits the name inline. */
  onRename?: (doc: HomeDoc, name: string) => void | Promise<void>;
  /** Adds "Move to folder…" to the ⋯ menu. The scene owns the chooser. */
  onMoveToFolder?: (doc: HomeDoc) => void;
  /** The folder this document is filed in, shown on the top-level list. */
  folderLabel?: string | null;
}

export function HomeDocCard({
  doc,
  onLoad,
  loading,
  onOpenExternal,
  onDownloadBundle,
  menuBusy = null,
  onRename,
  onMoveToFolder,
  folderLabel = null,
}: HomeDocCardProps): React.ReactElement {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const hasMenu = Boolean(onOpenExternal || onDownloadBundle || onMoveToFolder || onRename);
  const [editing, setEditing] = React.useState(false);
  const [draftName, setDraftName] = React.useState(doc.title);

  const commitRename = () => {
    setEditing(false);
    const next = draftName.trim();
    if (onRename && next && next !== doc.title) void onRename(doc, next);
  };

  const pill = docCardPill(doc.state, doc.stateLabel);
  const meta = [doc.sub, formatCardDate(doc.createdAt)].filter(Boolean).join(" · ");
  const totalMatch = /(\d+)\s+of\s+(\d+)/.exec(doc.progressLabel);
  const done = totalMatch ? Number(totalMatch[1]) : 0;
  const total = totalMatch ? Number(totalMatch[2]) : 0;

  const runMenuAction = (action: (d: HomeDoc) => void) => () => {
    setMenuOpen(false);
    action(doc);
  };

  return (
    <div className="as-doccard-wrap" data-current={doc.current ? "true" : undefined}>
      <div
        className="as-doccard"
        role="button"
        tabIndex={0}
        aria-label={`Load ${doc.title}`}
        aria-busy={loading || undefined}
        onClick={() => !loading && !editing && onLoad(doc)}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if ((e.key === "Enter" || e.key === " ") && !loading) {
            e.preventDefault();
            onLoad(doc);
          }
        }}
      >
        <span className="as-doccard-top">
          <span className="as-doccard-tags">
            <Tag tone="brand">{doc.doctype}</Tag>
            {folderLabel ? (
              <span className="as-tag" title={`In folder ${folderLabel}`} data-testid="home-doc-folder-tag">
                {folderLabel}
              </span>
            ) : null}
          </span>
          <span className="as-doccard-status">
            {loading ? <span className="as-spin" aria-label="Loading" /> : null}
            <StatusPill status={pill.status}>{pill.label}</StatusPill>
            {hasMenu ? <span className="as-doccard-menuslot" aria-hidden="true" /> : null}
          </span>
        </span>
        <span style={{ minWidth: 0 }}>
          {editing ? (
            <input
              className="as-input as-doccard-rename"
              value={draftName}
              // biome-ignore lint/a11y/noAutofocus: inline rename starts typing at once
              autoFocus
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => setDraftName(e.target.value)}
              onBlur={commitRename}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitRename();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  setEditing(false);
                }
              }}
              aria-label="Draft name"
            />
          ) : (
            <span className="as-doccard-name" style={{ display: "block" }}>
              {doc.title}
            </span>
          )}
          {meta ? <span className="as-doccard-meta">{meta}</span> : null}
        </span>
        {total > 0 ? <ProgressBar value={done} total={total} /> : null}
      </div>

      {hasMenu ? (
        <div className="as-doccard-menu">
          <button
            type="button"
            className="as-iconbtn"
            aria-label="More actions"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <Icon name="more" />
          </button>
          {menuOpen ? (
            <>
              <button
                type="button"
                aria-hidden
                tabIndex={-1}
                className="as-menu-backdrop"
                onClick={() => setMenuOpen(false)}
              />
              <div className="as-menu" role="menu">
                {onRename ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="as-menu-item"
                    onClick={() => {
                      setMenuOpen(false);
                      setDraftName(doc.title);
                      setEditing(true);
                    }}
                  >
                    Rename…
                  </button>
                ) : null}
                {onOpenExternal ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="as-menu-item"
                    disabled={menuBusy !== null}
                    onClick={runMenuAction(onOpenExternal)}
                  >
                    {menuBusy === "open" ? "Opening…" : "Open in Word"}
                  </button>
                ) : null}
                {onDownloadBundle ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="as-menu-item"
                    disabled={menuBusy !== null}
                    onClick={runMenuAction(onDownloadBundle)}
                  >
                    {menuBusy === "bundle" ? "Zipping…" : "Download bundle"}
                  </button>
                ) : null}
                {onMoveToFolder ? (
                  <button type="button" role="menuitem" className="as-menu-item" onClick={runMenuAction(onMoveToFolder)}>
                    Move to folder…
                  </button>
                ) : null}
              </div>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
